import { safeLocalStorage } from './storage';
import { ThemePreference } from '../types/user';

/**
 * The resolved theme — an actual colour scheme to render.
 *
 * Deliberately narrower than ThemePreference: `system` is a *choice*, not a
 * palette, and it has to survive as one. If the two were the same type, storing
 * the resolved value would quietly rewrite a user's "follow my system" into a
 * fixed light or dark the first time it was saved (#378).
 */
export type ThemeMode = 'light' | 'dark';

const THEME_STORAGE_KEY = 'drone-fleet-theme';

/**
 * The last known preference, cached locally.
 *
 * This is not the source of truth — the server is (#377) — but it is what lets
 * the correct theme paint on the very first frame, before any request has
 * finished. Without it, every load would flash the wrong colours.
 */
export const getStoredTheme = (): ThemePreference | null => {
  const storage = safeLocalStorage();
  if (!storage) {
    return null;
  }
  try {
    const stored = storage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch (e) {
    console.warn('Failed to read theme from localStorage:', e);
  }
  return null;
};

export const setStoredTheme = (theme: ThemePreference): void => {
  const storage = safeLocalStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(THEME_STORAGE_KEY, theme);
  } catch (e) {
    console.warn('Failed to save theme to localStorage:', e);
  }
};

export const getSystemTheme = (): ThemeMode => {
  if (
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark';
  }
  return 'light';
};

/** Turns a preference into the palette to actually render. */
export const resolveTheme = (preference: ThemePreference): ThemeMode =>
  preference === 'system' ? getSystemTheme() : preference;

/**
 * The preference to start from before the server has answered.
 *
 * Falls back to `system` rather than a fixed colour: with nothing stored, the
 * OS setting is the closest thing to a choice the user has actually made.
 */
export const getInitialThemePreference = (): ThemePreference =>
  getStoredTheme() ?? 'system';

/** The palette to paint on the first frame. */
export const getInitialTheme = (): ThemeMode =>
  resolveTheme(getInitialThemePreference());

/**
 * Subscribes to OS colour-scheme changes, returning an unsubscribe function.
 *
 * Only meaningful while the preference is `system`: someone who chose light or
 * dark explicitly should not have the app change under them when their laptop
 * switches at sunset.
 */
export const watchSystemTheme = (
  onChange: (mode: ThemeMode) => void
): (() => void) => {
  if (!window.matchMedia) {
    return () => undefined;
  }
  const query = window.matchMedia('(prefers-color-scheme: dark)');
  const listener = (event: MediaQueryListEvent) =>
    onChange(event.matches ? 'dark' : 'light');

  // addEventListener is not available on MediaQueryList in older Safari, where
  // addListener is the only option. jsdom is in the same position.
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }
  query.addListener(listener);
  return () => query.removeListener(listener);
};
